import { gql } from "@apollo/client";
import {
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
} from "@apollo/client/errors";
import { GraphQLQuery } from "@aws-amplify/api";
import { useSelector } from "app/hooks";
import { client } from "graphql/client";
import { generateReportPDFRequest } from "graphql/mutations";
import { onGenerateReportPDFRequest } from "graphql/subscriptions";
import { contributionProducts, withdrawalProducts } from "helpers/products";
import { useEffect, useRef, useState } from "react";
import { logAnalyticsEvent } from "services/analytics";

export const useDownloadReportButton = () => {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const {
    clientName,
    product,
    drawdownPercent,
    hasDrawdown,
    drawdownType,
    drawdownMonetaryAmount,
    hasRegular,
    hasSingle,
  } = useSelector((state) => state.investmentDetails);
  const {
    clientFunds,
    investmentAmount: totalInvestmentAmount,
    netAllocation,
  } = useSelector((state) => state.investmentAmount);
  const {
    selectedFunds: funds,
    reportingPeriod,
    isCustomPeriod,
    customStartDate,
  } = useSelector((state) => state.investmentFunds);
  const { amcRebate, fbrc } = useSelector((state) => state.charges);
  const selectedFunds = funds
    .filter(({ allocation }) => allocation && allocation > 0)
    .map(({ name, citicode, allocation, amc }) => ({
      name,
      citicode,
      allocation: allocation as number,
      amc: parseFloat((amc as string)?.replace("%", "")),
    }));

  const {
    adviserName,
    firmName: adviserFirm,
    welcomeNoteSelected,
    welcomeNote,
    finalNoteSelected,
    finalNote,
    logoImageId,
    logoImage,
    logoScale,
    includeFundDescriptionsSection,
    includeHighLevelAssetAllocationSection,
    includeRegionalEquityBreakdownSection,
    includeDiscretePortfolioPerformanceSection,
    includeIndividualFundPerformanceSection,
    includeRiskProfileSection,
    includeTermsAndChargesSection,
  } = useSelector((state) => state.personaliseReport);

  const { includeAdditionalExpenses } = useSelector((state) => state.charges);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [reportUrl, setReportUrl] = useState<string | undefined>();

  function closeModal() {
    logAnalyticsEvent({
      category: "Report details",
      action: "Page 3",
      label: "Cancel button",
    });
    setIsModalOpen(false);
    setReportUrl(undefined);
    if (timer.current) {
      clearTimeout(timer.current);
    }
  }

  function viewReport() {
    logAnalyticsEvent({
      category: "Report details",
      action: "Page 3",
      label: "View report button",
    });
    closeModal();
  }

  type GenerateReportPDFRequestMutation = {
    generateReportPDFRequest: {
      __typename: "generateReportPDFRequest";
      ID: string;
      Status: string;
      url: string;
    };
  };

  type Fund = {
    name: string;
    citicode: string;
    allocation: number;
  };

  type ReportResultPDFInput = {
    logoImageId: string | null;
    logoScale: number | null;
    clientName: string;
    product: string;
    drawdownPercent: number | undefined;
    drawdownMonetaryAmount: number | undefined;
    adviserName: string;
    adviserFirm: string;
    welcomeNote: string;
    finalNote: string;
    selectedFunds: Fund[];
    clientFunds: number;
    totalInvestmentAmount: number;
    netAllocation: number;
    amcRebate: number;
    fbrc: number;
    reportingPeriod?: number;
    customStartDate?: string;
    includeAdditionalExpenses: boolean;
    includeFundDescriptionsSection: boolean;
    includeHighLevelAssetAllocationSection: boolean;
    includeRegionalEquityBreakdownSection: boolean;
    includeDiscretePortfolioPerformanceSection: boolean;
    includeIndividualFundPerformanceSection: boolean;
    includeRiskProfileSection: boolean;
    includeTermsAndChargesSection: boolean;
  };

  useEffect(() => {
    const timerValue = timer.current;
    return () => {
      if (timer) {
        clearTimeout(timerValue);
      }
    };
  }, []);

  const handleCreateReport = async () => {
    setReportUrl(undefined);
    setIsModalOpen(true);
    // automatically close the modal after 5 minutes as the pdf signed url will have expired
    timer.current = setTimeout(() => setIsModalOpen(false), 5 * 60 * 1000);

    logAnalyticsEvent({
      category: "Report details",
      action: "Page 3",
      label: "Create report button",
    });

    if (localStorage.getItem("rememberCustomisationOptions") === "true") {
      if (logoImage) {
        localStorage.setItem("logoImage", logoImage);
        localStorage.setItem(
          "logoScale",
          logoScale ? logoScale.toString() : "100",
        );
      } else {
        localStorage.removeItem("logoImage");
        localStorage.removeItem("logoScale");
      }
      localStorage.setItem("firmName", adviserFirm.trim());
      localStorage.setItem("adviserName", adviserName.trim());
    } else {
      localStorage.removeItem("logoImage");
      localStorage.removeItem("logoScale");
      localStorage.removeItem("firmName");
      localStorage.removeItem("adviserName");
    }

    const input: ReportResultPDFInput = {
      logoImageId,
      logoScale,
      clientName,
      product,
      drawdownPercent:
        hasDrawdown &&
        drawdownType === "percent" &&
        withdrawalProducts.includes(product)
          ? drawdownPercent
          : undefined,
      drawdownMonetaryAmount:
        (hasDrawdown &&
          drawdownType === "monetary" &&
          withdrawalProducts.includes(product)) ||
        (contributionProducts.includes(product) && hasRegular)
          ? drawdownMonetaryAmount
          : undefined,
      adviserName,
      adviserFirm,
      welcomeNote: welcomeNoteSelected ? welcomeNote : "",
      finalNote: finalNoteSelected ? finalNote : "",
      selectedFunds,
      clientFunds:
        contributionProducts.includes(product) && hasSingle === false
          ? 0
          : parseFloat(clientFunds),
      totalInvestmentAmount:
        contributionProducts.includes(product) && hasSingle === false
          ? 0
          : totalInvestmentAmount,
      netAllocation: parseFloat(netAllocation),
      amcRebate: parseFloat(amcRebate),
      fbrc: parseFloat(fbrc),
      ...(isCustomPeriod && customStartDate
        ? { customStartDate }
        : { reportingPeriod: reportingPeriod as number }),
      includeAdditionalExpenses,
      includeFundDescriptionsSection,
      includeHighLevelAssetAllocationSection,
      includeRegionalEquityBreakdownSection,
      includeDiscretePortfolioPerformanceSection,
      includeIndividualFundPerformanceSection,
      includeRiskProfileSection,
      includeTermsAndChargesSection,
    };

    const { data } = await client.mutate<
      GraphQLQuery<GenerateReportPDFRequestMutation>
    >({ mutation: gql(generateReportPDFRequest), variables: { input } });

    // Ensure we have an ID before setting up subscription
    const reportId = data?.generateReportPDFRequest.ID;
    if (!reportId) {
      console.error("No report ID received from mutation");
      return;
    }

    console.info("Setting up subscription for report ID:", reportId);

    const observable = client.subscribe<{
      onGenerateReportPDFRequest: {
        ID: string;
        Status: string | null;
        url: string | null;
      };
    }>({
      query: gql(onGenerateReportPDFRequest),
      variables: {
        ID: reportId,
      },
    });

    const subscription = observable.subscribe({
      next: (result) => {
        console.info("Subscription data received:", result);
        const response = result.data?.onGenerateReportPDFRequest;
        if (response?.url) {
          setReportUrl(response.url);
        }
        subscription.unsubscribe();
      },
      error: (error) => {
        console.error("Subscription error:", error);
        if (CombinedGraphQLErrors.is(error) && error.errors[0]) {
          console.error("GraphQL error:", error.errors[0]);
        }
        if (CombinedProtocolErrors.is(error)) {
          console.error("Protocol error:", error.errors);
        }
      },
    });
  };

  return {
    isModalOpen,
    closeModal,
    reportUrl,
    handleCreateReport,
    viewReport,
  };
};
